'use client'
import { FC, ReactNode, useCallback, useMemo } from 'react'
import { MdClose, MdOutlineMenu } from 'react-icons/md'

import { useDispatch, useSelector } from 'shared-redux'
import { usePathname } from 'next/navigation'
import {
  selectedMenuCategory,
  selectIsWindowOnTop,
  setSelectedMenuCategory
} from 'shared-redux/state'
import { CustomSlideOutPayloadProps, useModal, useSlideOut } from '#/src/custom/controllers'
import { CategoryDTO, FooterItem, RequiredPageProps, SocialMediaItem } from 'ecosystem'
import { useAppConfigContext } from '#/src/appConfig'
import SlideOutCart from '#/src/custom/controllers/slideouts/SlideOutCart'
import CartSlideOutFooter from '#/src/custom/controllers/slideouts/CartSlideOutFooter'
import { CartButton, MenuItem, TopNav, TopNavSpacer } from 'ui/storefront'
import CustomFooter from '#/src/custom/layout/CustomFooter'
import { LinkProps } from '@chakra-ui/react'
import { useCustomCart } from '#/src/custom/cart/useCustomCart'
import { PageLayoutContainer } from 'storefront-modules/page'
import { SearchComponent } from 'storefront-modules/search'
import { useCustomSearch } from '#/src/custom/search/hooks/useCustomSearch'
import { AihubMobileTrigger } from 'storefront-modules/aihub'
import { filterCategories } from 'shared-utils'
import { UIMainMenuItem, UITopNavMenuItem } from 'storefront-modules/layout/Menu'
import { NavBarAction } from 'ui/storefront/layout/TopNavContent'
import { TopNavLogo } from '#/src/custom/layout/TopNavLogo'
import { useUIGlobalsContext } from 'ui/lib/ui-globals/hooks'

export type CustomLayoutProps = RequiredPageProps & {
  children: ReactNode | ReactNode[]
}

const CustomLayout: FC<CustomLayoutProps> = ({
  children,
  footerWidgets,
  staticPages,
  categories
}) => {
  const appConfig = useAppConfigContext()
  const { menuMaxContentWidth } = useUIGlobalsContext()
  const { isHidden, closeSlideOut, openSlideOut, componentId, overrideComponent } = useSlideOut()
  const openedMenuCategory = useSelector(selectedMenuCategory)
  const dispatch = useDispatch()
  const { isHidden: isModalHidden, size: modalSize } = useModal()

  const { cartCount, isCartOpen } = useCustomCart()

  const pathname = usePathname()
  const isWindowOnTop = useSelector(selectIsWindowOnTop)
  const isMenuDesktopOpen = !isHidden && componentId === 'categories'

  const handleCategoriesToggle = useCallback(
    (items: CategoryDTO[]) => {
      if (!items.length) {
        return closeSlideOut()
      }

      const slideOutProps = {
        title: 'Categories',
        componentId: 'categories' as const,
        shouldUnmountComponentAfterClose:
          appConfig.menuCategoriesConfig?.desktop?.variant === 'multi-column',
        drawerProps: {
          sx: {
            '.slide-out__content-container': {
              pt: '0'
            }
          }
        }
      }

      if (!isHidden && componentId !== 'categories') {
        overrideComponent(slideOutProps)
      } else if (isHidden) {
        openSlideOut(slideOutProps)
      }
    },
    [
      appConfig.menuCategoriesConfig?.desktop?.variant,
      closeSlideOut,
      componentId,
      isHidden,
      openSlideOut,
      overrideComponent
    ]
  )

  const categoryItems = useMemo<UIMainMenuItem<CategoryDTO>[]>(() => {
    const convertCategories = (categoriesList?: CategoryDTO[]) => {
      return filterCategories(categoriesList || []).map((item) => {
        const path = `${appConfig.paths.categoriesUrlPath}/${item.slug}`

        const menuItem: UIMainMenuItem<CategoryDTO> = {
          id: item.slug,
          text: item.name,
          path,
          type: 'store-front-category',
          originalData: item,
          ...(item.subCategories?.length ? { options: convertCategories(item.subCategories) } : {})
        }
        return menuItem
      })
    }
    return convertCategories(categories)
  }, [appConfig.paths.categoriesUrlPath, categories])

  const fullMenuCategoryItems = useMemo<UITopNavMenuItem[]>(() => {
    return categoryItems.map((item) => {
      const isActive = isMenuDesktopOpen && openedMenuCategory?.id === item.id
      const subCategories = item.originalData?.subCategories || []
      const expandable = !!item.originalData?.subCategories?.length

      const menuItem: UITopNavMenuItem = {
        ...item,
        expanded: isActive,
        expandable,
        onHoverEnter: () => {
          if (!isActive) {
            handleCategoriesToggle(subCategories)
            dispatch(setSelectedMenuCategory(item.originalData || null))
          }
        }
      }

      return menuItem
    })
  }, [categoryItems, dispatch, handleCategoriesToggle, isMenuDesktopOpen, openedMenuCategory?.id])

  const fullMenuOtherItems = useMemo<UITopNavMenuItem[]>(() => {
    return [
      ...(staticPages
        ? staticPages.map(
            (sp) =>
              ({
                id: 'other-' + sp.id,
                text: sp.name,
                path: sp.slug ? `/${sp.slug}` : '/',
                onHoverEnter: () => isMenuDesktopOpen && closeSlideOut(),
                type: 'other'
              }) satisfies UITopNavMenuItem
          )
        : []),
      ...appConfig.staticLinks.map(
        ({ label, ...s }) =>
          ({
            ...s,
            text: label,
            onHoverEnter: () => isMenuDesktopOpen && closeSlideOut(),
            type: 'other'
          }) satisfies UITopNavMenuItem
      )
    ]
  }, [appConfig.staticLinks, staticPages, isMenuDesktopOpen, closeSlideOut])

  const menuItems: {
    categories: UITopNavMenuItem[]
    other: UITopNavMenuItem[]
  } = useMemo(() => {
    return {
      categories: fullMenuCategoryItems,
      other: fullMenuOtherItems
    }
  }, [fullMenuCategoryItems, fullMenuOtherItems])

  const panelItems = appConfig.topNavPanel?.items

  const socialMediaItems: SocialMediaItem<LinkProps>[] = appConfig.socialMediaItems

  const footerItems: FooterItem[] = footerWidgets.map(({ widget }) => widget)

  const customSearch = useCustomSearch()

  const onSearch = useCallback(() => {
    const slideOutProps: CustomSlideOutPayloadProps<void> = {
      title: 'Search',
      componentId: 'search' as const,
      shouldUnmountComponentAfterClose: true,
      drawerProps: {
        sx: {
          bg: 'transparent',
          px: '0',
          boxShadow: 'none',
          '.slide-out__content-container': {
            py: '0',
            maxWidth: menuMaxContentWidth,
            margin: 'auto',
            bg: 'none'
          },
          '.slide-out__content-body': {
            px: '0'
          }
        }
      }
    }

    if (!isHidden && componentId !== 'search') {
      overrideComponent(slideOutProps)
    } else if (isHidden) {
      openSlideOut(slideOutProps)
    } else {
      closeSlideOut()
    }
  }, [closeSlideOut, componentId, isHidden, menuMaxContentWidth, openSlideOut, overrideComponent])

  const navbarActions: NavBarAction[] = [
    ...(process.env.NEXT_PUBLIC_AI_DEALS_ENABLED === 'true' ||
    (process.env.NEXT_PUBLIC_AI_ASSISTANT_ENABLED === 'true' && !!appConfig.assistant)
      ? [
          {
            component: <AihubMobileTrigger />,
            showsOnMobile: true
          }
        ]
      : []),
    ...(appConfig.showSearchNav !== false
      ? [
          {
            component: (
              <SearchComponent
                onSearch={onSearch}
                {...customSearch}
                componentsProps={appConfig.theme?.components?.searchComponents}
              />
            ),
            showsOnMobile: true
          }
        ]
      : []),
    ...(appConfig.showCart
      ? [
          {
            component: (
              <CartButton
                badgeCount={cartCount}
                isHidden={pathname === '/checkout'}
                isOpen={isCartOpen}
                path="/checkout"
              >
                <SlideOutCart />
                <CartSlideOutFooter />
              </CartButton>
            ),
            showsOnMobile: true
          }
        ]
      : [])
  ].map((navBarAction, index) => ({
    ...navBarAction,
    id: index
  }))

  const handleOnMenuToggle = (menuItems: MenuItem[]) => {
    if (isHidden) {
      openSlideOut({
        title: '',
        componentId: 'mobileMenu',
        footerId: 'mobileMenu',
        extraArgs: {
          menuItems
        },
        drawerProps: null
      })
    } else {
      closeSlideOut()
    }
  }

  const handleOnMenuHover = (isInSide: boolean) => {
    if (isInSide && componentId === 'categories') {
      closeSlideOut()
    }
  }

  return (
    <PageLayoutContainer
      pageContextProps={{
        categories: categories || [],
        footerWidgets,
        staticPages
      }}
      position="relative"
    >
      <TopNav
        contentHeight={appConfig.topNavHeight.content}
        logo={<TopNavLogo />}
        onMenuHover={handleOnMenuHover}
        onMenuToggle={handleOnMenuToggle}
        panelHeight={isWindowOnTop ? appConfig.topNavHeight.panel : 0}
        toggleIcon={isHidden ? <MdOutlineMenu /> : <MdClose />}
        zIndex={!isModalHidden && modalSize === 'full' ? 0 : 9000}
        {...{ panelItems, menuItems, navbarActions, isHidden }}
      />
      <TopNavSpacer
        h={isWindowOnTop ? appConfig.topNavHeight.spacer : { base: '8vh', md: '14vh' }}
      />
      {children}
      <CustomFooter {...{ footerItems, socialMediaItems }} />
    </PageLayoutContainer>
  )
}

export default CustomLayout
